import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { INSIGHTS as SEED_INSIGHTS } from "../lib/content";
import { ArrowUpRight, Search } from "lucide-react";
import { BRAND } from "../lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = "All";

const CATEGORY_IMAGE = {
  "All": "https://images.unsplash.com/photo-1582610116397-edb318620f90?auto=format&fit=crop&q=80&w=1600",
  "Holiday Homes": "https://images.unsplash.com/photo-1582610116397-edb318620f90?auto=format&fit=crop&q=80&w=1600",
  "Plot Investment": "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&q=80&w=1600",
  "Investment Basics": "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=1600",
  "Market Updates": "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=1600",
};

export default function Insights() {
  const [cmsArticles, setCmsArticles] = useState([]);

  useEffect(() => {
    axios.get(`${API}/insights`).then((r) => setCmsArticles(r.data || [])).catch(() => {});
  }, []);

  // Merge: CMS articles take precedence by slug, otherwise seed.
  const ALL_ARTICLES = useMemo(() => {
    const cmsSlugs = new Set(cmsArticles.map((a) => a.slug));
    return [...cmsArticles, ...SEED_INSIGHTS.filter((s) => !cmsSlugs.has(s.slug))];
  }, [cmsArticles]);

  const categories = [ALL, ...Array.from(new Set(ALL_ARTICLES.map((a) => a.category)))];
  const [active, setActive] = useState(ALL);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => ALL_ARTICLES.filter((a) => {
    const okCategory = active === ALL || a.category === active;
    const okQuery = !query || (a.title + " " + a.summary).toLowerCase().includes(query.toLowerCase());
    return okCategory && okQuery;
  }), [ALL_ARTICLES, active, query]);

  const featured = filtered[0];
  const rest = filtered.slice(1);
  const featuredImage = CATEGORY_IMAGE[active] || CATEGORY_IMAGE.All;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="insights-page">
      <div className="text-xs uppercase tracking-widest text-[#1D4ED8]">Insights</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 max-w-4xl leading-[1.1]">
        Designed to help you make better decisions — not to create urgency.
      </h1>

      <div className="mt-10 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setActive(c)}
              data-testid={`category-tab-${c.replace(/\s+/g, '-').toLowerCase()}`}
              className={`text-xs uppercase tracking-widest rounded-full px-4 py-2 border transition-all ${
                active === c
                  ? "bg-[#1D4ED8] text-white border-[#1D4ED8]"
                  : "bg-white text-zinc-700 border-zinc-200 hover:border-[#1D4ED8] hover:text-[#1D4ED8]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles..."
            className="pl-9 pr-4 py-2.5 rounded-full border border-zinc-200 bg-white text-sm w-full md:w-64 focus:outline-none focus:border-[#1D4ED8]"
            data-testid="insights-search"
          />
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="mt-16 text-center py-16 border border-dashed border-zinc-300 rounded-3xl">
          <div className="font-display text-2xl">No articles match.</div>
          <p className="text-zinc-500 mt-2">Try another category or clear your search.</p>
        </div>
      )}

      {featured && (
        <Link
          to={`/insights/${featured.slug}`}
          className="block mt-10 bg-white border border-zinc-200 rounded-3xl overflow-hidden hover:border-[#1D4ED8] transition-all group animate-fade-in"
          data-testid="featured-article"
        >
          <div className="grid md:grid-cols-2">
            <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
              <img key={featuredImage} src={featuredImage} alt={featured.category} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 animate-fade-in" />
            </div>
            <div className="p-10 flex flex-col justify-center">
              <div className="text-xs uppercase tracking-widest text-[#1D4ED8]">{featured.category}</div>
              <h2 className="font-display text-3xl md:text-4xl mt-3 leading-tight">{featured.title}</h2>
              <p className="text-zinc-600 mt-4">{featured.summary}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-[#1D4ED8] font-medium">
                Read the article <ArrowUpRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </Link>
      )}

      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rest.map((a) => (
          <Link
            key={a.slug}
            to={`/insights/${a.slug}`}
            className="bg-white border border-zinc-200 rounded-2xl p-7 hover:border-[#1D4ED8] hover:-translate-y-1 transition-all animate-fade-in"
            data-testid={`article-${a.slug}`}
          >
            <div className="text-xs uppercase tracking-widest text-[#1D4ED8]">{a.category}</div>
            <h3 className="font-display text-2xl mt-2 leading-snug">{a.title}</h3>
            <p className="text-zinc-600 mt-3 text-sm line-clamp-3">{a.summary}</p>
          </Link>
        ))}
      </div>

      <div className="mt-20 bg-gradient-to-br from-[#1D4ED8] to-[#1E40AF] text-white rounded-3xl p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden">
        <div aria-hidden className="absolute -right-20 -bottom-20 w-[320px] h-[320px] rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="relative">
          <h3 className="font-display text-3xl sm:text-4xl">Reading is a great start. The next step is a 1:1 conversation.</h3>
          <p className="text-white/85 mt-3 max-w-lg">A 45-minute Clarity Call — no developer on the line, no follow-up sales calls.</p>
        </div>
        <Link to="/clarity-call" className="inline-flex items-center gap-2 bg-white text-[#1D4ED8] hover:bg-zinc-100 px-6 py-3 rounded-full font-medium relative">
          Book a Clarity Call
        </Link>
      </div>
    </div>
  );
}
